/**
 * Square / Mono primitives — THE vocabulary for the product surface.
 *
 * These were written inline in CycleDetail during its conversion. They are here
 * because the second page to convert would otherwise have copied them, and the
 * eleven-way drift of the icon button (see ./iconButton.ts) is what that looks
 * like a few months later. Every page converted from the old rounded/gray system
 * imports from this module; none of them redefine a button, chip, or label.
 *
 * ── The cascade rule these encode ──────────────────────────────────────────
 * src/index.css has two hand-written dark rules:
 *     .dark *      { border-color: var(--rule) }      (0,1,0)
 *     .dark h1…h6  { color: var(--text-pri) }         (0,1,1)
 * Tailwind 3 emits no native cascade layers in this build, so what decides is
 * specificity, then order — and both rules come after Tailwind's plain
 * utilities in the built sheet (measured in the running app, 2026-10-01). A
 * plain `border-[var(--mustard)]` (0,1,0) ties with `.dark *` and loses on
 * order, so it renders grey in dark mode; a plain text class on a heading is
 * outweighed. Inline styles sit above the whole author cascade, so BORDER
 * COLOUR and HEADING COLOUR go in `style` — one declaration, both modes.
 * A `dark:` variant class wins too (it is (0,2,0), and Tailwind writes variant
 * classes last), but it covers dark only.
 * Backgrounds and non-heading text have no such override and stay token classes
 * — which is also what lets `hover:` work at all.
 *
 * That split is why most helpers return `{className, style}` to be SPREAD onto
 * the element rather than a bare string. Spread them; don't unpack them.
 */
import type React from 'react';

/** Visible keyboard focus for every interactive element. */
export const FOCUS_RING =
  'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--mustard)]';

/** Semantic tones. Each token pair is defined in BOTH the light and dark palette. */
export type Tone = 'green' | 'mustard' | 'slate' | 'coral' | 'neutral';

export const TONE_BORDER: Record<Tone, string> = {
  green: 'var(--green)',
  mustard: 'var(--mustard)',
  slate: 'var(--slate)',
  coral: 'var(--coral)',
  neutral: 'var(--rule-bright)',
};
/** Forest and clay words use the word tokens (src/index.css): the accent in
    light, --text-ter in Night, where forest and clay are for lines and fills. */
export const TONE_TEXT: Record<Tone, string> = {
  green: 'text-[var(--green-text)]',
  mustard: 'text-[var(--mustard)]',
  slate: 'text-[var(--text-ter)]',
  coral: 'text-[var(--coral-text)]',
  neutral: 'text-[var(--text-ter)]',
};
/** Words on the slate wash are --text-sec, not --text-ter: in light mode
    --text-ter on the wash is 4.45:1 (under 4.5); --text-sec is 7.61:1. */
export const TONE_FILL: Record<Tone, string> = {
  green: 'bg-[var(--green-dim)] text-[var(--green-text)]',
  mustard: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  slate: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  coral: 'bg-[var(--coral-dim)] text-[var(--coral-text)]',
  neutral: 'bg-[var(--surface)] text-[var(--text-ter)]',
};

/** Square panel on the raised background. Spread onto the element. */
export const panel = (extra = '') => ({
  className: `border bg-[var(--bg-raised)] ${extra}`.trim(),
  style: { borderColor: 'var(--rule)' } as React.CSSProperties,
});

/** Inset panel — a quieter surface for a block nested inside a panel. */
export const panelInset = (extra = '') => ({
  className: `border bg-[var(--surface)] ${extra}`.trim(),
  style: { borderColor: 'var(--rule)' } as React.CSSProperties,
});

/** Mono eyebrow / field label. */
export const LABEL_MONO =
  'font-mono text-[0.78rem] uppercase tracking-[0.12em] text-[var(--text-ter)]';
const LABEL_COLOR: React.CSSProperties = { color: 'var(--text-ter)' };

/** Spread onto an <h1>–<h6> that should read as a mono eyebrow. The inline
    colour is required: `.dark h1…h6` would otherwise outweigh the class. */
export const heading = (extra = '') => ({
  className: `${LABEL_MONO} ${extra}`.trim(),
  style: LABEL_COLOR,
});

/** Shared button geometry — adjacent buttons are always the same height and box. */
export const BTN_BASE =
  `inline-flex items-center justify-center gap-1.5 h-9 whitespace-nowrap px-3.5 border font-mono text-[0.75rem] uppercase tracking-[0.08em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${FOCUS_RING}`;

/** Primary action: solid slate, white ink. */
export const btnPrimary = (extra = '') => ({
  className: `${BTN_BASE} bg-[var(--slate)] text-white hover:opacity-90 ${extra}`.trim(),
  style: { borderColor: 'var(--slate)' } as React.CSSProperties,
});

/** Secondary action: transparent with a bright rule. Same box as primary. */
export const btnSecondary = (extra = '') => ({
  className: `${BTN_BASE} text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] ${extra}`.trim(),
  style: { borderColor: 'var(--rule-bright)' } as React.CSSProperties,
});

/** Destructive action: coral ink and rule. Same box again. */
export const btnDanger = (extra = '') => ({
  className: `${BTN_BASE} text-[var(--coral)] hover:bg-[var(--coral-dim)] ${extra}`.trim(),
  style: { borderColor: 'var(--coral)' } as React.CSSProperties,
});

/**
 * Status READOUT — colour and glyph, never a box.
 *
 * THE AFFORDANCE RULE: a bordered box means "you can press this". Status is
 * read, not pressed, so it must not borrow the button's affordance. Before this
 * existed, a green "Active" pill was visually identical to the button beside it
 * and there was no way to tell which one did something.
 */
export const status = (tone: Tone, extra = '') => ({
  className:
    `inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[0.78rem] uppercase tracking-[0.08em] ${TONE_TEXT[tone]} ${extra}`.trim(),
});

/** Boxed chip — for a tagged VALUE inside a dense list (a scope, a category),
    or something pressable. Never for bare status; use `status` for that. */
export const chip = (tone: Tone, extra = '') => ({
  className:
    `inline-flex items-center gap-1.5 px-2.5 py-1 border font-mono text-[0.74rem] uppercase tracking-[0.08em] whitespace-nowrap ${TONE_FILL[tone]} ${extra}`.trim(),
  style: { borderColor: TONE_BORDER[tone] } as React.CSSProperties,
});

/** Square form control (select / input). */
export const field = (extra = '') => ({
  className: `w-full border bg-[var(--surface)] px-3 py-2 text-sm text-[var(--text-pri)] ${FOCUS_RING} ${extra}`.trim(),
  style: { borderColor: 'var(--rule-bright)' } as React.CSSProperties,
});

/** A row in a list: full-bleed, ruled underneath, tinted on hover. */
export const row = (extra = '') => ({
  className: `flex items-center gap-3 border-b px-4 py-3 transition-colors hover:bg-[var(--hover-tint)] ${extra}`.trim(),
  style: { borderColor: 'var(--rule)' } as React.CSSProperties,
});
